import { Link } from "react-router-dom";
import StatusBadge from "./StatusBadge";
import { categoryLabels } from "@/lib/vehicleMeta";
import type { Vehicle } from "@/types";

interface VehicleCardProps {
  vehicle: Vehicle;
  index?: number;
}

export default function VehicleCard({ vehicle, index = 0 }: VehicleCardProps) {
  const unavailable = vehicle.estado !== "disponible";

  return (
    <article
      className="group flex flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-all duration-300 hover:-translate-y-1 hover:border-primary-300 animate-fade-up"
      style={{ animationDelay: `${index * 60}ms` }}
    >
      <div className="relative h-52 w-full overflow-hidden bg-background-100">
        <img
          src={vehicle.imagen_url}
          alt={`${vehicle.nombre} — ${categoryLabels[vehicle.categoria]} de AXONIA GROUP`}
          title={`${vehicle.nombre} — ${vehicle.tipo}`}
          className={`h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105 ${
            unavailable ? "opacity-70" : ""
          }`}
        />
        <div className="absolute left-4 top-4">
          <StatusBadge status={vehicle.estado} />
        </div>
        <span className="absolute right-4 top-4 rounded-full bg-primary-50/95 px-2.5 py-1 text-xs font-label font-semibold text-secondary-900 backdrop-blur-sm">
          {categoryLabels[vehicle.categoria]}
        </span>
      </div>

      <div className="flex flex-1 flex-col p-5">
        <h3 className="text-base font-heading font-semibold text-foreground-950 md:text-lg">
          {vehicle.nombre}
        </h3>
        <p className="mt-1 flex items-center gap-1.5 text-xs font-label text-foreground-500">
          <i className="ri-equalizer-2-line text-sm leading-none" aria-hidden="true"></i>
          {vehicle.tipo}
        </p>

        <p className="mt-3 text-sm leading-relaxed text-foreground-600">{vehicle.descripcion}</p>

        <div className="mt-4 flex flex-wrap gap-1.5">
          <span className="inline-flex items-center gap-1.5 rounded-md bg-secondary-50 px-2.5 py-1 text-xs font-label font-medium text-secondary-900">
            <i className="ri-scales-3-line text-sm leading-none" aria-hidden="true"></i>
            {vehicle.capacidad}
          </span>
          {vehicle.caracteristicas.slice(0, 2).map((feature) => (
            <span
              key={feature}
              className="inline-flex items-center gap-1.5 rounded-md bg-background-100 px-2.5 py-1 text-xs font-label text-foreground-600"
            >
              <i className="ri-check-line text-sm leading-none text-accent-600" aria-hidden="true"></i>
              {feature}
            </span>
          ))}
        </div>

        <p className="mt-4 flex items-center gap-1.5 text-xs text-foreground-500">
          <i className="ri-map-pin-line text-sm leading-none" aria-hidden="true"></i>
          {vehicle.zona}
        </p>

        <div className="mt-5 flex-1"></div>
        <Link
          to={`/solicitar-servicio?unidad=${vehicle.id}`}
          className={`inline-flex w-full items-center justify-center gap-2 rounded-md px-4 py-2.5 text-sm font-label font-semibold transition-colors duration-200 ${
            unavailable
              ? "bg-background-200 text-foreground-600 hover:bg-background-300"
              : "bg-primary-500 text-background-50 hover:bg-primary-600 dark:text-foreground-950"
          }`}
        >
          {unavailable ? "Consultar disponibilidad" : "Solicitar esta unidad"}
          <i className="ri-arrow-right-line text-base leading-none" aria-hidden="true"></i>
        </Link>
      </div>
    </article>
  );
}